/**
 * Serviço de Busca Avançada
 * Implementa busca híbrida: API backend + cache local para autocomplete
 */

import { FileItem, searchTranscriptions, getAutocomplete, getSearchFiltersOptions } from '../../../services/api'
import type {
  ApiSearchRequest,
  ApiSearchResponse,
  ApiAutocompleteResponse,
  ApiSearchFiltersOptions
} from '../../../services/api'
import {
  Transcription,
  TranscriptSegment,
  Entity,
  EntityType,
  SentimentLabel,
  AISummary,
  UserAnnotations
} from '../../../types/transcription'
import {
  SearchQuery,
  SearchResponse,
  SearchResults,
  SearchInsights,
  TranscriptMatch,
  EntityMatch,
  FileMatch,
  SummaryMatch,
  AnnotationMatch,
  ParsedSearchQuery,
  SearchSuggestion,
  Contradiction,
  TimelineItem,
  DEFAULT_SEARCH_QUERY
} from '../types/search.types'

// ═══════════════════════════════════════════════════════════════
// CACHE DE DADOS PARA BUSCA
// ═══════════════════════════════════════════════════════════════

interface SearchableFile {
  file: FileItem
  transcription?: Transcription
  entities?: Entity[]
  summary?: AISummary
  annotations?: UserAnnotations
  speakers: string[]
}

// Cache local de arquivos indexados
const searchCache: Map<string, SearchableFile> = new Map()

/**
 * Indexa um arquivo para busca
 */
export function indexFileForSearch(
  file: FileItem,
  transcription?: Transcription,
  entities?: Entity[],
  summary?: AISummary,
  annotations?: UserAnnotations
): void {
  const speakers = new Set<string>()

  if (transcription?.transcript) {
    transcription.transcript.forEach(seg => {
      if (seg.speaker) speakers.add(seg.speaker)
    })
  }

  searchCache.set(file.fileId, {
    file,
    transcription,
    entities,
    summary,
    annotations,
    speakers: Array.from(speakers)
  })
}

/**
 * Remove arquivo do índice
 */
export function removeFileFromIndex(fileId: string): void {
  searchCache.delete(fileId)
}

/**
 * Limpa todo o cache
 */
export function clearSearchCache(): void {
  searchCache.clear()
}

/**
 * Retorna todos os falantes únicos
 */
export function getAllSpeakers(): string[] {
  const speakers = new Set<string>()
  searchCache.forEach(item => {
    item.speakers.forEach(s => speakers.add(s))
  })
  return Array.from(speakers).sort()
}

// ═══════════════════════════════════════════════════════════════
// PARSER DE QUERY
// ═══════════════════════════════════════════════════════════════

/**
 * Parse da query de busca para extrair termos, frases e filtros
 */
export function parseSearchQuery(query: string): ParsedSearchQuery {
  const result: ParsedSearchQuery = {
    terms: [],
    phrases: [],
    inlineFilters: [],
    excludedTerms: []
  }

  if (!query.trim()) return result

  let processedQuery = query

  // Mapeia aliases de campos de filtro inline
  const fieldMap: Record<string, string> = {
    'falante': 'speaker',
    'speaker': 'speaker',
    'arquivo': 'file',
    'file': 'file',
    'tipo': 'entityType',
    'type': 'entityType',
    'sentimento': 'sentiment',
    'sentiment': 'sentiment',
    'pasta': 'folder',
    'folder': 'folder',
    'data': 'date',
    'date': 'date',
    'anotado': 'annotated',
    'annotated': 'annotated'
  }

  // PASSO 1: Extrai filtros inline ANTES das frases exatas
  // Isso previne que "Daniel" em falante:"Daniel" seja capturado como frase
  const filterRegex = /(\w+):\s*(?:"([^"]+)"|(\S+))/g
  processedQuery = processedQuery.replace(filterRegex, (fullMatch, field, quotedValue, bareValue) => {
    const normalizedField = field.toLowerCase()
    const value = quotedValue || bareValue

    if (fieldMap[normalizedField]) {
      result.inlineFilters.push({
        field: fieldMap[normalizedField],
        value,
        operator: 'contains'
      })
      return ' ' // Remove filtro reconhecido do texto
    }
    return fullMatch // Mantém campo:valor não reconhecido
  })

  // Remove prefixos de filtro órfãos (ex: "falante:" sem valor)
  const knownFields = Object.keys(fieldMap).join('|')
  processedQuery = processedQuery.replace(new RegExp(`\\b(?:${knownFields}):\\s*`, 'gi'), ' ')

  // PASSO 2: Extrai frases exatas (entre aspas)
  const phraseRegex = /"([^"]+)"/g
  let match
  while ((match = phraseRegex.exec(processedQuery)) !== null) {
    result.phrases.push(match[1])
  }
  processedQuery = processedQuery.replace(/"([^"]+)"/g, ' ')

  // Extrai termos excluídos (NOT ou -)
  const excludeRegex = /(?:NOT\s+|-)([\w]+)/gi
  while ((match = excludeRegex.exec(processedQuery)) !== null) {
    result.excludedTerms.push(match[1].toLowerCase())
    processedQuery = processedQuery.replace(match[0], ' ')
  }

  // Remove operadores AND/OR e extrai termos restantes
  processedQuery = processedQuery.replace(/\b(AND|OR)\b/gi, ' ')

  const terms = processedQuery
    .split(/\s+/)
    .map(t => t.trim().toLowerCase())
    .filter(t => t.length > 0)

  result.terms = terms

  return result
}

// ═══════════════════════════════════════════════════════════════
// FUNÇÕES DE MATCHING
// ═══════════════════════════════════════════════════════════════

/**
 * Verifica se um texto contém os termos da busca
 */
function matchesQuery(
  text: string,
  parsed: ParsedSearchQuery,
  operator: 'AND' | 'OR'
): { matches: boolean; score: number } {
  if (!text) return { matches: false, score: 0 }

  const lowerText = text.toLowerCase()
  let score = 0

  // Verifica termos excluídos primeiro
  for (const excluded of parsed.excludedTerms) {
    if (lowerText.includes(excluded)) {
      return { matches: false, score: 0 }
    }
  }

  // Verifica frases exatas
  let phraseMatches = 0
  for (const phrase of parsed.phrases) {
    if (lowerText.includes(phrase.toLowerCase())) {
      phraseMatches++
      score += 10 // Frases exatas têm peso maior
    }
  }

  // Verifica termos
  let termMatches = 0
  for (const term of parsed.terms) {
    if (lowerText.includes(term)) {
      termMatches++
      score += 1
    }
  }

  // Aplica operador
  const totalRequired = parsed.phrases.length + parsed.terms.length
  const totalMatches = phraseMatches + termMatches

  if (totalRequired === 0) {
    return { matches: true, score: 0 }
  }

  if (operator === 'AND') {
    return {
      matches: totalMatches === totalRequired,
      score: totalMatches === totalRequired ? score : 0
    }
  } else {
    return {
      matches: totalMatches > 0,
      score
    }
  }
}

/**
 * Destaca os termos encontrados no texto
 */
export function highlightMatches(text: string, parsed: ParsedSearchQuery): string {
  if (!text) return ''

  let result = text

  // Destaca frases primeiro (para não quebrar com termos individuais)
  for (const phrase of parsed.phrases) {
    const regex = new RegExp(`(${escapeRegex(phrase)})`, 'gi')
    result = result.replace(regex, '<mark>$1</mark>')
  }

  // Destaca termos
  for (const term of parsed.terms) {
    const regex = new RegExp(`(${escapeRegex(term)})`, 'gi')
    result = result.replace(regex, '<mark>$1</mark>')
  }

  return result
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Extrai contexto ao redor de um match
 */
function extractContext(
  segments: TranscriptSegment[],
  segmentIndex: number,
  maxChars: number = 100
): { before: string; after: string } {
  let before = ''
  let after = ''

  // Contexto anterior
  if (segmentIndex > 0) {
    const prevSegment = segments[segmentIndex - 1]
    before = prevSegment.text.slice(-maxChars)
    if (prevSegment.text.length > maxChars) {
      before = '...' + before
    }
  }

  // Contexto posterior
  if (segmentIndex < segments.length - 1) {
    const nextSegment = segments[segmentIndex + 1]
    after = nextSegment.text.slice(0, maxChars)
    if (nextSegment.text.length > maxChars) {
      after = after + '...'
    }
  }

  return { before, after }
}

// ═══════════════════════════════════════════════════════════════
// FUNÇÃO PRINCIPAL DE BUSCA
// ═══════════════════════════════════════════════════════════════

/**
 * Executa busca avançada
 */
export async function executeSearch(
  query: SearchQuery,
  files: FileItem[],
  getTranscription?: (fileId: string) => Promise<Transcription | null>,
  getEntities?: (fileId: string) => Promise<Entity[] | null>,
  getSummary?: (fileId: string) => Promise<AISummary | null>
): Promise<SearchResponse> {
  const startTime = performance.now()

  const parsed = parseSearchQuery(query.query)

  // Se query está vazia e não há filtros, retorna vazio
  if (
    parsed.terms.length === 0 &&
    parsed.phrases.length === 0 &&
    parsed.inlineFilters.length === 0
  ) {
    return createEmptyResponse()
  }

  const results: SearchResults = {
    transcriptMatches: [],
    entityMatches: [],
    fileMatches: [],
    summaryMatches: [],
    annotationMatches: []
  }

  // Aplica filtros nos arquivos
  let filteredFiles = files.filter(f => {
    // Filtro por status
    if (query.filters.fileStatus?.length) {
      if (!query.filters.fileStatus.includes(f.status as any)) {
        return false
      }
    }

    // Filtro por pasta
    if (query.filters.folders?.length) {
      if (!query.filters.folders.includes(f.folderId || '')) {
        return false
      }
    }

    // Filtro por arquivos específicos
    if (query.filters.fileIds?.length) {
      if (!query.filters.fileIds.includes(f.fileId)) {
        return false
      }
    }

    return true
  })

  // Busca em cada arquivo
  for (const file of filteredFiles) {
    // Busca no nome do arquivo (title e displayName)
    if (query.searchIn.filenames) {
      const nameToSearch = file.displayName || file.title || ''
      const { matches, score } = matchesQuery(nameToSearch, parsed, query.operator)
      if (matches) {
        results.fileMatches.push({
          file,
          matchCount: 1,
          preview: nameToSearch,
          highlightedPreview: highlightMatches(nameToSearch, parsed),
          relevanceScore: score
        })
      }
    }

    // Busca em transcrições (se disponível no cache)
    const cached = searchCache.get(file.fileId)

    if (query.searchIn.transcriptions && cached?.transcription) {
      const transcription = cached.transcription

      transcription.transcript.forEach((segment, index) => {
        // Aplica filtro de falante
        if (query.filters.speakers?.length) {
          if (!query.filters.speakers.includes(segment.speaker)) {
            return
          }
        }

        // Aplica filtro inline de falante
        const speakerFilter = parsed.inlineFilters.find(f => f.field === 'speaker')
        if (speakerFilter) {
          if (!segment.speaker.toLowerCase().includes(speakerFilter.value.toLowerCase())) {
            return
          }
        }

        // Aplica filtro de confiança
        if (query.filters.minConfidence !== undefined) {
          if ((segment.confidence || 1) < query.filters.minConfidence) {
            return
          }
        }

        const { matches, score } = matchesQuery(segment.text, parsed, query.operator)

        if (matches) {
          const context = extractContext(transcription.transcript, index)

          results.transcriptMatches.push({
            fileId: file.fileId,
            fileName: file.displayName || file.title || 'Sem título',
            segmentIndex: index,
            speaker: segment.speaker,
            text: segment.text,
            highlightedText: highlightMatches(segment.text, parsed),
            timestamp: {
              start: segment.start_time,
              end: segment.end_time
            },
            confidence: segment.confidence || 1,
            context,
            relevanceScore: score
          })
        }
      })
    }

    // Busca em entidades
    if (query.searchIn.entities && cached?.entities) {
      for (const entity of cached.entities) {
        // Aplica filtro de tipo de entidade
        if (query.filters.entityTypes?.length) {
          if (!query.filters.entityTypes.includes(entity.type)) {
            continue
          }
        }

        const { matches, score } = matchesQuery(entity.value, parsed, query.operator)

        if (matches) {
          results.entityMatches.push({
            entity: {
              type: entity.type,
              value: entity.value,
              normalizedValue: entity.normalizedValue
            },
            fileId: file.fileId,
            fileName: file.displayName || file.title || 'Sem título',
            occurrenceCount: entity.occurrences?.length || 1,
            occurrences: (entity.occurrences || []).map(occ => ({
              segmentIndex: 0,
              context: occ.context,
              timestamp: '',
              speaker: ''
            })),
            relevanceScore: score
          })
        }
      }
    }

    // Busca em resumos
    if (query.searchIn.summaries && cached?.summary) {
      const summary = cached.summary

      // Busca no resumo principal
      if (summary.summary) {
        const { matches, score } = matchesQuery(summary.summary, parsed, query.operator)
        if (matches) {
          results.summaryMatches.push({
            fileId: file.fileId,
            fileName: file.displayName || file.title || 'Sem título',
            matchType: 'summary',
            highlightedText: highlightMatches(summary.summary, parsed),
            relevanceScore: score
          })
        }
      }

      // Busca nos pontos-chave
      if (summary.keyPoints) {
        for (const point of summary.keyPoints) {
          const { matches, score } = matchesQuery(point, parsed, query.operator)
          if (matches) {
            results.summaryMatches.push({
              fileId: file.fileId,
              fileName: file.displayName || file.title || 'Sem título',
              matchType: 'keyPoint',
              highlightedText: highlightMatches(point, parsed),
              relevanceScore: score
            })
          }
        }
      }
    }

    // Busca em anotações
    if (query.searchIn.annotations && cached?.annotations) {
      const annotations = cached.annotations

      // Busca em notas
      if (annotations.annotations) {
        for (const annotation of annotations.annotations) {
          const { matches, score } = matchesQuery(annotation.text, parsed, query.operator)
          if (matches) {
            results.annotationMatches.push({
              fileId: file.fileId,
              fileName: file.displayName || file.title || 'Sem título',
              annotationType: 'note',
              text: annotation.text,
              highlightedText: highlightMatches(annotation.text, parsed),
              segmentIndex: annotation.segmentIndex,
              relevanceScore: score
            })
          }
        }
      }
    }
  }

  // Ordena resultados por relevância
  results.transcriptMatches.sort((a, b) => b.relevanceScore - a.relevanceScore)
  results.entityMatches.sort((a, b) => b.relevanceScore - a.relevanceScore)
  results.fileMatches.sort((a, b) => b.relevanceScore - a.relevanceScore)
  results.summaryMatches.sort((a, b) => b.relevanceScore - a.relevanceScore)
  results.annotationMatches.sort((a, b) => b.relevanceScore - a.relevanceScore)

  // Calcula total
  const totalResults =
    results.transcriptMatches.length +
    results.entityMatches.length +
    results.fileMatches.length +
    results.summaryMatches.length +
    results.annotationMatches.length

  // Gera insights
  const insights = generateInsights(results, parsed)

  // Gera sugestões
  const suggestions = generateSuggestions(query.query, results)

  const queryTime = performance.now() - startTime

  return {
    totalResults,
    page: query.page,
    pageSize: query.pageSize,
    queryTime: Math.round(queryTime),
    results,
    insights,
    suggestions
  }
}

// ═══════════════════════════════════════════════════════════════
// GERAÇÃO DE INSIGHTS
// ═══════════════════════════════════════════════════════════════

function generateInsights(results: SearchResults, parsed: ParsedSearchQuery): SearchInsights {
  const insights: SearchInsights = {
    contradictions: [],
    topPeople: [],
    topLocations: [],
    eventTimeline: [],
    sentimentDistribution: {
      positive: 0,
      neutral: 0,
      negative: 0
    }
  }

  // Conta pessoas mencionadas
  const peopleCounts = new Map<string, { count: number; files: Set<string> }>()

  for (const entity of results.entityMatches) {
    if (entity.entity.type === 'PERSON') {
      const name = entity.entity.value
      const existing = peopleCounts.get(name) || { count: 0, files: new Set() }
      existing.count += entity.occurrenceCount
      existing.files.add(entity.fileId)
      peopleCounts.set(name, existing)
    }
  }

  insights.topPeople = Array.from(peopleCounts.entries())
    .map(([name, data]) => ({
      name,
      count: data.count,
      fileCount: data.files.size
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // Conta locais
  const locationCounts = new Map<string, number>()

  for (const entity of results.entityMatches) {
    if (entity.entity.type === 'LOCATION') {
      const name = entity.entity.value
      locationCounts.set(name, (locationCounts.get(name) || 0) + entity.occurrenceCount)
    }
  }

  insights.topLocations = Array.from(locationCounts.entries())
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // Detecta contradições potenciais (simplificado)
  // Agrupa por falante e busca padrões conflitantes
  const statementsByPerson = new Map<string, TranscriptMatch[]>()

  for (const match of results.transcriptMatches) {
    const speaker = match.speaker
    const existing = statementsByPerson.get(speaker) || []
    existing.push(match)
    statementsByPerson.set(speaker, existing)
  }

  // Busca por padrões de tempo conflitantes
  const timePatterns = /(\d{1,2})\s*(hora|h|:)/gi

  statementsByPerson.forEach((statements, speaker) => {
    if (statements.length < 2) return

    const timeStatements: { match: TranscriptMatch; time: number }[] = []

    for (const stmt of statements) {
      const timeMatch = timePatterns.exec(stmt.text)
      if (timeMatch) {
        timeStatements.push({
          match: stmt,
          time: parseInt(timeMatch[1])
        })
      }
      timePatterns.lastIndex = 0
    }

    // Compara tempos mencionados
    for (let i = 0; i < timeStatements.length; i++) {
      for (let j = i + 1; j < timeStatements.length; j++) {
        const diff = Math.abs(timeStatements[i].time - timeStatements[j].time)
        if (diff >= 1 && diff <= 3) {
          insights.contradictions.push({
            type: 'time',
            severity: diff === 1 ? 'low' : diff === 2 ? 'medium' : 'high',
            statementA: {
              fileId: timeStatements[i].match.fileId,
              fileName: timeStatements[i].match.fileName,
              segmentIndex: timeStatements[i].match.segmentIndex,
              speaker: timeStatements[i].match.speaker,
              text: timeStatements[i].match.text,
              timestamp: timeStatements[i].match.timestamp.start
            },
            statementB: {
              fileId: timeStatements[j].match.fileId,
              fileName: timeStatements[j].match.fileName,
              segmentIndex: timeStatements[j].match.segmentIndex,
              speaker: timeStatements[j].match.speaker,
              text: timeStatements[j].match.text,
              timestamp: timeStatements[j].match.timestamp.start
            },
            analysis: `Possível inconsistência temporal: ${speaker} menciona horários diferentes (${timeStatements[i].time}h vs ${timeStatements[j].time}h)`
          })
        }
      }
    }
  })

  return insights
}

function generateSuggestions(query: string, results: SearchResults): string[] {
  const suggestions: string[] = []

  // Sugere refinar por falante se houver múltiplos
  const speakers = new Set<string>()
  results.transcriptMatches.forEach(m => speakers.add(m.speaker))

  if (speakers.size > 1 && query.length > 0) {
    const firstSpeaker = Array.from(speakers)[0]
    suggestions.push(`${query} falante:"${firstSpeaker}"`)
  }

  // Sugere buscar em entidades se houver resultados
  if (results.entityMatches.length > 0 && query.length > 0) {
    const firstEntity = results.entityMatches[0]
    suggestions.push(`tipo:${firstEntity.entity.type.toLowerCase()} "${firstEntity.entity.value}"`)
  }

  return suggestions.slice(0, 3)
}

function createEmptyResponse(): SearchResponse {
  return {
    totalResults: 0,
    page: 1,
    pageSize: 20,
    queryTime: 0,
    results: {
      transcriptMatches: [],
      entityMatches: [],
      fileMatches: [],
      summaryMatches: [],
      annotationMatches: []
    },
    insights: {
      contradictions: [],
      topPeople: [],
      topLocations: [],
      eventTimeline: [],
      sentimentDistribution: { positive: 0, neutral: 0, negative: 0 }
    },
    suggestions: []
  }
}

// ═══════════════════════════════════════════════════════════════
// AUTOCOMPLETE E SUGESTÕES
// ═══════════════════════════════════════════════════════════════

/**
 * Gera sugestões de autocomplete
 */
export function getAutocompleteSuggestions(
  query: string,
  searchHistory: string[],
  files: FileItem[]
): SearchSuggestion[] {
  const suggestions: SearchSuggestion[] = []
  const lowerQuery = query.toLowerCase()

  if (!query.trim()) {
    // Retorna histórico recente
    return searchHistory.slice(0, 5).map(h => ({
      text: h,
      type: 'history' as const
    }))
  }

  // Busca em histórico
  searchHistory
    .filter(h => h.toLowerCase().includes(lowerQuery))
    .slice(0, 2)
    .forEach(h => {
      suggestions.push({ text: h, type: 'history' })
    })

  // Busca em nomes de arquivo
  files
    .filter(f => f.title?.toLowerCase().includes(lowerQuery))
    .slice(0, 3)
    .forEach(f => {
      suggestions.push({
        text: `arquivo:"${f.title}"`,
        type: 'file',
        metadata: { fileId: f.fileId }
      })
    })

  // Busca em falantes conhecidos
  const allSpeakers = getAllSpeakers()
  allSpeakers
    .filter(s => s.toLowerCase().includes(lowerQuery))
    .slice(0, 2)
    .forEach(s => {
      suggestions.push({
        text: `falante:"${s}"`,
        type: 'speaker'
      })
    })

  // Busca em entidades no cache
  searchCache.forEach(cached => {
    if (cached.entities) {
      cached.entities
        .filter(e => e.value.toLowerCase().includes(lowerQuery))
        .slice(0, 2)
        .forEach(e => {
          const existing = suggestions.find(s => s.text.includes(e.value))
          if (!existing) {
            suggestions.push({
              text: `"${e.value}"`,
              type: 'entity',
              metadata: { entityType: e.type }
            })
          }
        })
    }
  })

  return suggestions.slice(0, 8)
}

// ═══════════════════════════════════════════════════════════════
// BUSCA VIA API (RECOMENDADO - USA BACKEND)
// ═══════════════════════════════════════════════════════════════

/**
 * Executa busca avançada via API do backend.
 * Esta é a função recomendada para uso em produção.
 *
 * SEGURANÇA: Os dados são filtrados no backend por tennant e player,
 * garantindo isolamento completo entre usuários.
 *
 * @param query Parâmetros da busca no formato frontend
 * @returns Resultados adaptados para o formato do frontend
 */
export async function executeSearchApi(query: SearchQuery): Promise<SearchResponse> {
  const startTime = performance.now()

  // Extrai filtros inline do texto da query
  const parsed = parseSearchQuery(query.query)

  const inlineSpeakers = parsed.inlineFilters.filter(f => f.field === 'speaker').map(f => f.value)
  const inlineFileTitle = parsed.inlineFilters.find(f => f.field === 'file')?.value
  const inlineSentiments = parsed.inlineFilters.filter(f => f.field === 'sentiment').map(f => f.value)
  const inlineEntityTypes = parsed.inlineFilters.filter(f => f.field === 'entityType').map(f => f.value)
  const inlineFolder = parsed.inlineFilters.find(f => f.field === 'folder')?.value
  const inlineAnnotated = parsed.inlineFilters.find(f => f.field === 'annotated')
  const inlineHasAnnotations = inlineAnnotated
    ? (inlineAnnotated.value.toLowerCase() !== 'false' && inlineAnnotated.value !== '0')
    : undefined

  // Mescla filtros do painel com filtros inline
  const mergedSpeakers = [...(query.filters.speakers ?? []), ...inlineSpeakers]
  const mergedSentiment = [...(query.filters.sentiment ?? []), ...inlineSentiments]
  const mergedEntityTypes = [...((query.filters.entityTypes as string[] | undefined) ?? []), ...inlineEntityTypes]

  // Reconstrói texto de busca limpo (sem sintaxe de filtros inline)
  const cleanQuery = [
    ...parsed.phrases.map(p => `"${p}"`),
    ...parsed.terms,
    ...parsed.excludedTerms.map(t => `-${t}`)
  ].join(' ').trim()

  // Converte para formato da API
  const apiRequest: ApiSearchRequest = {
    query: cleanQuery || (parsed.inlineFilters.length > 0 ? '' : query.query),
    scope: {
      titles: query.searchIn.filenames,
      entities: query.searchIn.entities,
      summaries: query.searchIn.summaries,
      annotations: query.searchIn.annotations,
      timeline: true,
      topics: true
    },
    filters: {
      speakers: mergedSpeakers.length > 0 ? mergedSpeakers : undefined,
      sentiment: mergedSentiment.length > 0 ? mergedSentiment : undefined,
      entityTypes: mergedEntityTypes.length > 0 ? mergedEntityTypes : undefined,
      processingState: query.filters.fileStatus?.[0],
      folderId: inlineFolder ?? query.filters.folders?.[0],
      hasAnnotations: inlineHasAnnotations ?? query.filters.hasAnnotations,
      fileTitle: inlineFileTitle
    },
    page: query.page - 1, // API usa 0-indexed
    pageSize: query.pageSize,
    sort: `${query.sortBy === 'relevance' ? 'matchCount' : 'fileCreatedAt'},${query.sortOrder}`
  }

  try {
    const apiResponse = await searchTranscriptions(apiRequest)

    // Adapta para formato do frontend
    return adaptApiResponse(apiResponse, query, performance.now() - startTime)
  } catch (error) {
    console.error('Erro na busca via API:', error)
    // Fallback para busca local se API falhar
    return createEmptyResponse()
  }
}

/**
 * Adapta resposta da API para formato do frontend
 */
function adaptApiResponse(
  api: ApiSearchResponse,
  query: SearchQuery,
  localQueryTime: number
): SearchResponse {
  return {
    totalResults: api.totalResults,
    page: api.page + 1, // Frontend usa 1-indexed
    pageSize: api.pageSize,
    queryTime: Math.round(api.queryTime || localQueryTime),
    results: {
      transcriptMatches: [], // API não retorna transcript matches diretamente
      entityMatches: api.results.entities.map(e => ({
        entity: {
          type: e.entityType as any,
          value: e.entityValue,
          normalizedValue: e.normalizedValue || undefined
        },
        fileId: e.fileId,
        fileName: e.fileName,
        occurrenceCount: e.occurrenceCount,
        occurrences: [],
        relevanceScore: e.relevance
      })),
      fileMatches: api.results.files.map(f => ({
        file: {
          fileId: f.fileId,
          title: f.title,
          type: null,
          bytes: 0,
          status: f.processingState === 'COMPLETED' ? 'completed' : 'processing',
          currentState: f.processingState,
          currentSubState: null,
          progress: f.progressPercent,
          message: null,
          createdAt: f.createdAt?.toString() || null,
          completedAt: null,
          folderId: f.folderId
        },
        matchCount: f.matchCount,
        preview: f.preview,
        relevanceScore: f.matchCount
      })),
      summaryMatches: api.results.summaries.map(s => ({
        fileId: s.fileId,
        fileName: s.fileName,
        matchType: s.matchType as any,
        highlightedText: s.highlightedText,
        relevanceScore: 1
      })),
      annotationMatches: api.results.annotations.map(a => ({
        fileId: a.fileId,
        fileName: a.fileName,
        annotationType: a.annotationType as any,
        text: a.text,
        highlightedText: a.highlightedText,
        segmentIndex: a.segmentIndex,
        relevanceScore: 1
      }))
    },
    insights: {
      contradictions: [],
      topPeople: api.insights.topPeople.map(p => ({
        name: p.name,
        count: p.count,
        fileCount: p.fileIds.length
      })),
      topLocations: api.insights.topLocations.map(l => ({
        name: l.name,
        count: l.count
      })),
      eventTimeline: api.results.timeline.map(t => ({
        time: t.timestamp,
        description: t.description,
        fileId: t.fileId,
        fileName: t.fileName,
        speaker: t.participants[0] || '',
        segmentIndex: 0
      })),
      sentimentDistribution: api.insights.sentimentDistribution
    },
    suggestions: []
  }
}

/**
 * Detecta prefixo de filtro inline na query (ex: falante:Dan, arquivo:reu)
 * Retorna o prefixo original e o tipo correspondente, ou null se não há prefixo.
 */
function detectInlinePrefix(query: string): { prefix: string; type: string } | null {
  const match = query.match(/^(falante|speaker|arquivo|file|tipo|type):\s*"?/i)
  if (!match) return null

  const prefix = match[1].toLowerCase()
  const typeMap: Record<string, string> = {
    'falante': 'speaker',
    'speaker': 'speaker',
    'arquivo': 'file',
    'file': 'file',
    'tipo': 'entity',
    'type': 'entity'
  }

  return { prefix: match[1], type: typeMap[prefix] }
}

/**
 * Obtém sugestões de autocomplete via API.
 * Suporta filtros inline: quando o usuário digita "falante:Dan", o backend
 * extrai o termo "Dan", filtra apenas sugestões do tipo "speaker", e o
 * frontend reformata as sugestões com o prefixo correto.
 */
export async function getAutocompleteSuggestionsApi(
  query: string,
  limit: number = 10
): Promise<SearchSuggestion[]> {
  try {
    const response = await getAutocomplete(query, limit)

    // Detecta se o usuário está digitando com prefixo inline
    const inlinePrefix = detectInlinePrefix(query)

    return response.suggestions.map(s => {
      let text: string

      if (inlinePrefix) {
        // Reformata a sugestão com o prefixo inline original do usuário
        text = `${inlinePrefix.prefix}:"${s.text}"`
      } else if (s.type === 'entity') {
        text = `"${s.text}"`
      } else {
        text = s.text
      }

      return {
        text,
        type: s.type === 'topic' ? 'term' : s.type as any,
        metadata: s.entityType ? { entityType: s.entityType as any } : undefined
      }
    })
  } catch (error) {
    console.error('Erro no autocomplete via API:', error)
    return []
  }
}

/**
 * Obtém opções de filtros disponíveis via API
 */
export async function getSearchFiltersApi(): Promise<ApiSearchFiltersOptions | null> {
  try {
    return await getSearchFiltersOptions()
  } catch (error) {
    console.error('Erro ao obter filtros via API:', error)
    return null
  }
}
